import {Box, Button, Cluster, DateTime, FormItem, Stack, States, TextInput} from '@pluto-tv/assemble';
import {popoverActionsProps} from 'helpers/popoverActionProps';
import {DateTime as luxon} from 'luxon';
import * as React from 'react';

export interface IAvailabilityWindowFormProps {
  onCancel: () => void;
  onSave: (savedObj: IAvailbilityWindow) => void;
  value?: Partial<IAvailbilityWindow>;
}

export interface IAvailbilityWindow {
  _id?: string;
  screenings: number;
  startDate: string | Date;
  endDate: string | Date;
}

const AvailabilityWindowForm = React.memo(({onCancel, onSave, value}: IAvailabilityWindowFormProps) => {
  const [startDate, setStartDate] = React.useState<Date>();
  const [endDate, setEndDate] = React.useState<Date>();
  const [screeningsInput, setScreeningsInput] = React.useState<string | number | null>(-1);

  const [formState, setFormState] = React.useState<States>('disabled');
  const [endWarning, setEndWarning] = React.useState<string>();
  const [screenWarning, setScreenWarning] = React.useState<string>();
  const [isUpdating, setIsUpdating] = React.useState<boolean>();

  React.useEffect(() => {
    const now = new Date();

    if (value) {
      // Convert ISO dates from UTC to Los Angeles timezone before extracting the date
      // This ensures the date picker shows the correct date that was originally selected
      const startLuxon = luxon.fromISO(value.startDate as string, {zone: 'utc'}).setZone('America/Los_Angeles');
      const endLuxon = luxon.fromISO(value.endDate as string, {zone: 'utc'}).setZone('America/Los_Angeles');

      const startDayISO = startLuxon.toISODate();
      const endDayISO = endLuxon.toISODate();

      setStartDate(value.startDate ? luxon.fromISO(startDayISO, {zone: 'America/Los_Angeles'}).toJSDate() : now);
      setEndDate(value.endDate ? luxon.fromISO(endDayISO, {zone: 'America/Los_Angeles'}).toJSDate() : now);
      const screeningsValue = value.screenings ?? -1;
      setScreeningsInput(screeningsValue);
      setIsUpdating(true);
    } else {
      setStartDate(now);
      setEndDate(now);
      setScreeningsInput(-1);
      setIsUpdating(false);
    }
  }, [value]);

  React.useEffect(() => {
    if (!endDate || !startDate) {
      setFormState('disabled');
      return;
    }

    // Check if screenings input is empty
    if (screeningsInput === '' || screeningsInput === null || screeningsInput === undefined) {
      setFormState('disabled');
      setScreenWarning('Screenings is required');
      return;
    }

    // Parse screenings to number
    const screeningsNumber = Number(screeningsInput);

    // Check if screenings is invalid (NaN)
    if (isNaN(screeningsNumber)) {
      setFormState('disabled');
      setScreenWarning('Screenings must be a number');
      return;
    }

    // Check if screenings is less than -1
    if (screeningsNumber < -1) {
      setFormState('disabled');
      setScreenWarning('Screenings cannot be lower than -1');
      return;
    }

    if (endDate && startDate) {
      const luxonStart = luxon.fromJSDate(startDate);
      const luxonEnd = luxon.fromJSDate(endDate);

      if (luxonStart.startOf('day').toMillis() > luxonEnd.endOf('day').toMillis()) {
        setFormState('disabled');
        setEndWarning('End date must be after start date');
        return;
      }
    }

    setScreenWarning(undefined);
    setEndWarning(undefined);
    setFormState('');
  }, [endDate, startDate, screeningsInput]);

  const updateSaveWindow = () => {
    if (!endDate || !startDate) {
      return;
    }

    // Validate screenings input
    if (screeningsInput === '' || screeningsInput === null || screeningsInput === undefined) {
      return;
    }

    const screeningsNumber = Number(screeningsInput);
    if (isNaN(screeningsNumber) || screeningsNumber < -1) {
      return;
    }

    // Since we're saving everything to PDT, we need to strip the browser TZ info and
    // just start with the date from the picker. We'll parse that as PDT in the next step
    const startDayISO = luxon.fromJSDate(startDate).toISODate();
    const endDayISO = luxon.fromJSDate(endDate).toISODate();

    onSave({
      endDate: luxon.fromISO(endDayISO, {zone: 'America/Los_Angeles'}).endOf('day').toISO(),
      startDate: luxon.fromISO(startDayISO, {zone: 'America/Los_Angeles'}).startOf('day').toISO(),
      screenings: screeningsNumber,
    });
  };

  return (
    <Box
      padding={popoverActionsProps.padding}
      background={popoverActionsProps.background}
      width={popoverActionsProps.width}
    >
      <Stack space='small'>
        <FormItem label='Start Date'>
          <DateTime
            key={startDate?.getTime()}
            placeholder='Start Date'
            id='linearAvailWindowStartTime'
            value={startDate}
            onChange={val => setStartDate(val as Date)}
          />
        </FormItem>
        <FormItem label='End Date' helpText={endWarning} state={endWarning ? 'error' : ''}>
          <DateTime
            key={endDate?.getTime()}
            placeholder='End Date'
            id='linearAvailWindowEndTime'
            value={endDate}
            onChange={val => setEndDate(val as Date)}
          />
        </FormItem>
        <FormItem label='Screenings' helpText={screenWarning} state={screenWarning ? 'error' : ''}>
          <TextInput
            id='linearAvailWindowScreening'
            type='number'
            value={
              screeningsInput === '' || screeningsInput === null || screeningsInput === undefined
                ? undefined
                : Number(screeningsInput)
            }
            onChange={val => {
              // When type='number', onChange receives number, but can be null/undefined when cleared
              // Store as empty string when cleared to allow user to type new value
              if (val === null || val === undefined || (typeof val === 'number' && isNaN(val))) {
                setScreeningsInput('');
                return;
              }
              // Store as number - TextInput handles it as number but allows clearing
              setScreeningsInput(val);
            }}
          />
        </FormItem>
        <Cluster justify='space-between'>
          <div></div>
          <Cluster space='small'>
            <Button ghost={true} onClick={onCancel}>
              Cancel
            </Button>
            <Button id='saveLinearAvailableWindow' state={formState} type='primary' onClick={updateSaveWindow}>
              {!isUpdating ? '+ Add' : 'Update'}
            </Button>
          </Cluster>
        </Cluster>
      </Stack>
    </Box>
  );
});

AvailabilityWindowForm.displayName = 'AvailabilityWindowForm';
export default AvailabilityWindowForm;
